import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle,
  Play,
  Menu,
  X,
  Brain,
  BookMarked,
  BookOpen,
  Calendar,
  BarChart3,
  Heart,
  Sparkles,
  Sun,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

import { confirmSignUp, resendSignUpCode, signIn, getCurrentUser, fetchUserAttributes, signInWithRedirect } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/data';

import type { Schema } from '../../../amplify/data/resource';
import {
  clearConflictingAuthSessionForInvite,
  inviteEmailFromRecord,
  resetInviteLandingStorage,
  storeInviteLandingContext,
} from '../../lib/orgInviteLanding';
import {
  associateInvitedKidToParent,
  loadParentInvitePrefill,
  type ParentInvitePrefill,
} from '../../lib/parentInvitePrefill';

/**
 * `/parents` = public “Start free” → always PARENT self-serve (no invite context).
 * `/parent-referral?token=` = email invite link (same UI; token ties org/kid in register()).
 */
function clearInviteStorageIfParentsPublicSignup(pathname: string, search: string) {
  const token = new URLSearchParams(search).get('token');
  if (pathname === '/parents' && !token) {
    localStorage.removeItem('pendingInvitationToken');
    localStorage.removeItem('pendingInvitationPage');
  }
}

function LandingParentReferral() {
  const { register } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const client = useMemo(() => generateClient<Schema>(), []);

  useEffect(() => {
    clearInviteStorageIfParentsPublicSignup(location.pathname, location.search);
  }, [location.pathname, location.search]);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [invitePrefill, setInvitePrefill] = useState<ParentInvitePrefill | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const inviteToken = useMemo(
    () => new URLSearchParams(location.search).get('token'),
    [location.search],
  );
  const emailLocked = Boolean(invitePrefill?.email);

  useEffect(() => {
    if (!inviteToken) return;
    let active = true;
    setInviteLoading(true);
    setInviteError(null);
    void (async () => {
      try {
        resetInviteLandingStorage();
        storeInviteLandingContext(inviteToken, 'parent-referral');
        const { prefill, error: loadError } = await loadParentInvitePrefill(inviteToken);
        if (!active) return;
        if (loadError || !prefill) {
          setInviteError(loadError || 'Invalid invitation link.');
          setInvitePrefill(null);
          return;
        }
        const invitedEmail = inviteEmailFromRecord(prefill.email);
        await clearConflictingAuthSessionForInvite(invitedEmail);
        if (!active) return;
        setInvitePrefill(prefill);
        setForm({
          firstName: prefill.firstName,
          lastName: prefill.lastName,
          email: invitedEmail,
          password: '',
          confirmPassword: '',
          phoneNumber: prefill.phoneNumber,
        });
        setShowForm(true);
      } catch {
        if (!active) return;
        setInviteError('Error loading invitation details.');
      } finally {
        if (active) setInviteLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [inviteToken]);

  const parentFeatures = [
    {
      title: 'AI next-skill prediction',
      desc: "Our AI predicts your child's next skill so you know what to work on next.",
      Icon: Brain,
      accent: 'from-indigo-50 to-indigo-100',
      iconBg: 'bg-indigo-100 text-indigo-600'
    },
    {
      title: 'AI curriculum creation',
      desc: 'Build personalized curricula with AI—goals, milestones, and activities for your child.',
      Icon: BookMarked,
      accent: 'from-purple-50 to-purple-100',
      iconBg: 'bg-purple-100 text-purple-600'
    },
    {
      title: 'AI-guided reading & comprehension',
      desc: 'Reading and comprehension support with AI-guided, level-appropriate content.',
      Icon: BookOpen,
      accent: 'from-sky-50 to-sky-100',
      iconBg: 'bg-sky-100 text-sky-600'
    },
    {
      title: 'Daily progress tracker',
      desc: 'Day-by-day progress on goals and milestones so you never lose sight of growth.',
      Icon: Calendar,
      accent: 'from-rose-50 to-rose-100',
      iconBg: 'bg-rose-100 text-rose-600'
    },
    {
      title: 'Progress insights',
      desc: "Clear charts and insights show what's working and how your child is progressing.",
      Icon: BarChart3,
      accent: 'from-cyan-50 to-cyan-100',
      iconBg: 'bg-cyan-100 text-cyan-600'
    }
  ];

  // Google sign-in handler
  const handleGoogleSignIn = async () => {
    try {
      setError(null);

      clearInviteStorageIfParentsPublicSignup(location.pathname, location.search);

      // App.tsx uses pendingInvitationPage after OAuth; parent-referral keeps invite vs self-serve straight
      if (inviteToken) {
        storeInviteLandingContext(inviteToken, 'parent-referral');
      } else {
        localStorage.setItem('pendingInvitationPage', 'parent-referral');
      }
      
      await signInWithRedirect({ provider: 'Google', customState: 'prompt=select_account' });
    } catch (e) {
      const err = e as { message?: string };
      setError(err?.message || 'Google sign-in failed.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits for phone number and limit to 10 characters
    if (e.target.id === 'phoneNumber') {
      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
      setForm({ ...form, phoneNumber: value });
    } else if (e.target.id === 'email' && emailLocked) {
      return;
    } else {
      setForm({ ...form, [e.target.id]: e.target.value });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      clearInviteStorageIfParentsPublicSignup(location.pathname, location.search);

      const params = new URLSearchParams(location.search);
      const token = params.get('token') || inviteToken;
      if (token) {
        storeInviteLandingContext(token, 'parent-referral');
      }

      // Always PARENT on this page; invite token (when present) ties org/kid via register()
      await register(
        form.email,
        form.password,
        form.firstName,
        form.lastName,
        form.phoneNumber,
        invitePrefill?.organizationId || undefined,
        invitePrefill?.kidProfileId || undefined,
        'PARENT'
      );
      setRegisteredEmail(form.email);
      setShowVerification(true);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Error creating account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const createUserProfile = async () => {
    const currentUser = await getCurrentUser();
    const userAttributes = await fetchUserAttributes();
    const userId = currentUser.userId;

    // register() creates User with authMode: 'apiKey' before verify; userPool get often returns null (owner rules).
    const existingApi = await client.models.User.get({ id: userId }, { authMode: 'apiKey' });
    if (existingApi.data?.id) {
      return existingApi.data;
    }

    const existingPool = await client.models.User.get({ id: userId });
    if (existingPool.data?.id) {
      return existingPool.data;
    }

    const email = (userAttributes.email || form.email).toLowerCase().trim();
    let formattedPhone = form.phoneNumber;
    if (/^\d{10}$/.test(form.phoneNumber)) {
      formattedPhone = `+1${form.phoneNumber}`;
    }
    const userData: Record<string, unknown> = {
      id: userId,
      role: 'PARENT',
      status: 'ACTIVE',
      fName: userAttributes.given_name || form.firstName,
      lName: userAttributes.family_name || form.lastName,
      email,
      phoneNumber: (userAttributes.phone_number as string | undefined) || formattedPhone || '',
      username: email.split('@')[0] || 'user',
      address: 'Not provided',
      dob: new Date().toISOString().split('T')[0],
      mName: '-',
      password: 'email_auth_user',
    };

    const profile = await client.models.User.create(userData as never, { authMode: 'apiKey' });

    if (profile.errors?.length) {
      const msg = profile.errors.map((e) => e.message).filter(Boolean).join('; ');
      console.error('User.create after verify:', profile.errors);
      throw new Error(msg || 'Failed to create user profile');
    }

    if (!profile.data?.id) {
      throw new Error('Failed to create user profile');
    }

    return profile.data;
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      // Confirm the sign up with verification code
      await confirmSignUp({
        username: registeredEmail.toLowerCase().trim(),
        confirmationCode: verificationCode
      });

      // After successful confirmation, sign in the user
      const { nextStep } = await signIn({
        username: registeredEmail.toLowerCase().trim(),
        password: form.password,
        options: {
          authFlowType: "USER_SRP_AUTH"
        }
      });

      if (nextStep.signInStep === 'DONE') {
        // Create user profile with PARENT role
        const profile = await createUserProfile();
        const parentUserId = profile?.id || (await getCurrentUser()).userId;
        const token =
          inviteToken || localStorage.getItem('pendingInvitationToken') || '';

        let linkedKidId = invitePrefill?.kidProfileId || null;
        if (token && parentUserId) {
          const associated = await associateInvitedKidToParent({
            invitationToken: token,
            parentUserId,
          });
          linkedKidId = associated.kidProfileId || linkedKidId;
        }

        localStorage.removeItem('pendingInvitationToken');
        localStorage.removeItem('pendingInvitationPage');

        window.location.href = linkedKidId
          ? `/kid-profile/${linkedKidId}`
          : '/kid-profile-form';
      }
    } catch (error: any) {
      // Error confirming sign up logged
      if (error.name === 'CodeMismatchException') {
        setError('Invalid verification code. Please try again.');
      } else if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Error confirming account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setLoading(true);

    try {
      await resendSignUpCode({
        username: registeredEmail.toLowerCase().trim()
      });
      setError('A new verification code has been sent to your email.');
    } catch (error: any) {
      // Error resending code logged
      if (error.name === 'LimitExceededException') {
        setError('Too many attempts. Please wait a while before requesting a new code.');
      } else {
        setError('Error sending new code. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative isolate min-h-screen overflow-x-hidden bg-gradient-to-b from-amber-50/80 via-white to-cyan-50/40">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-pink-200/40 bg-white/80 shadow-sm shadow-rose-100/30 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              {/* Logo / branding — global Header includes Home for all pages */}
            </div>
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button
                type="button"
                className="text-slate-600 hover:text-indigo-600 transition-colors font-semibold"
                onClick={() => navigate('/organizations')}
              >
                Find a clinic
              </button>
              <a href="#how-it-works" className="text-slate-600 hover:text-pink-600 transition-colors font-semibold">
                How it works
              </a>
              <a href="#testimonials" className="text-slate-600 hover:text-violet-600 transition-colors font-semibold">
                Parent stories
              </a>
              <a href="#why-tinywins" className="text-slate-600 hover:text-cyan-600 transition-colors font-semibold">
                Why TinyWins
              </a>
              <a href="/" className="text-slate-600 hover:text-indigo-600 transition-colors font-semibold">
                For clinicians
              </a>
              <button
                className="rounded-full bg-gradient-to-r from-pink-500 via-orange-400 to-amber-400 px-6 py-2.5 font-bold text-white shadow-lg shadow-orange-200/50 transition hover:brightness-105 hover:shadow-orange-300/60"
                onClick={() => setShowForm(true)}
              >
                Get started — free
              </button>
            </nav>
            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-100">
              <div className="flex flex-col space-y-4">
                <button
                  type="button"
                  className="text-left text-gray-600 hover:text-gray-900 transition-colors font-medium w-full"
                  onClick={() => {
                    setIsMenuOpen(false);
                    navigate('/organizations');
                  }}
                >
                  Find a clinic
                </button>
                <a href="#how-it-works" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">
                  How It Works
                </a>
                <a href="#testimonials" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">
                  Parent Stories
                </a>
                <a href="#why-tinywins" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">
                  Why TinyWins
                </a>
                <a href="/" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">
                  For Clinicians
                </a>
                <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium w-fit" onClick={() => setShowForm(true)}>
                  Get Started - It's Free
                </button>
              </div>
            </div>
          )}
        </div>
      </header>
      {/* Hero Section */}
      {!showForm && (
      <section className="relative z-10 overflow-hidden py-16 lg:py-28">
        <div
          className="pointer-events-none absolute inset-0 opacity-55"
          aria-hidden
          style={{
            background:
              'radial-gradient(ellipse 90% 70% at 0% 0%, rgba(253, 224, 71, 0.35), transparent 50%), radial-gradient(ellipse 80% 60% at 100% 10%, rgba(244, 114, 182, 0.3), transparent 45%), radial-gradient(ellipse 70% 50% at 80% 100%, rgba(45, 212, 191, 0.28), transparent 40%)',
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <h1 className="mb-6 text-4xl font-black leading-[1.08] tracking-tight text-slate-900 lg:text-6xl lg:leading-[1.05]">
                Your child&apos;s story—{' '}
                <span className="bg-gradient-to-r from-pink-600 via-amber-500 to-teal-500 bg-clip-text text-transparent">
                  in full color.
                </span>
              </h1>
              <div className="mb-6 inline-flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full border border-amber-200/80 bg-white/90 px-4 py-2 text-xs font-bold uppercase tracking-wider text-amber-900 shadow-sm">
                  <Sun className="h-4 w-4 text-amber-500" />
                  Built for real families
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-pink-200/80 bg-white/90 px-4 py-2 text-xs font-bold uppercase tracking-wider text-pink-900 shadow-sm">
                  <Sparkles className="h-4 w-4 text-pink-500" />
                  HIPAA mindful
                </span>
              </div>
              <p className="mb-4 text-xl font-semibold leading-relaxed text-slate-700 lg:text-2xl">
                You&apos;re not just &quot;doing enough.&quot; You&apos;re showing up. TinyWins helps the world see the magic you already know is there.
              </p>
              <p className="mb-8 text-lg leading-relaxed text-slate-600">
                Warm, progress-based guidance that turns everyday wins into charts, next steps, and hope—personalized for
                neurodivergent kids and the adults who love them.
              </p>
              <div className="mb-8 flex flex-col gap-4 sm:flex-row">
                <button
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-pink-500 via-orange-500 to-amber-400 px-8 py-4 text-lg font-bold text-white shadow-xl shadow-orange-200/40 transition hover:brightness-105"
                  onClick={() => setShowForm(true)}
                >
                  Start free — see their progress
                  <ArrowRight className="h-5 w-5" />
                </button>
                <button className="inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-violet-200 bg-white/80 px-8 py-4 text-lg font-bold text-violet-800 shadow-sm backdrop-blur transition hover:border-violet-300 hover:bg-white">
                  <Play className="h-5 w-5 text-violet-600" />
                  Watch demo
                </button>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold text-slate-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 shrink-0 text-emerald-500" />
                  Free to start
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 shrink-0 text-sky-500" />
                  No credit card
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 shrink-0 text-fuchsia-500" />
                  No waitlist
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative z-10 rounded-3xl border-2 border-white/80 bg-white/95 p-6 shadow-2xl shadow-pink-200/30 backdrop-blur-sm ring-1 ring-pink-100/60 lg:p-8">
                <img
                  src="https://images.pexels.com/photos/8613089/pexels-photo-8613089.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Parent and child celebrating together"
                  className="mb-6 h-80 w-full rounded-xl object-cover"
                />
                <div className="space-y-4">
                  <div className="flex items-center justify-between rounded-lg bg-green-50 p-4">
                    <div className="flex items-center">
                      <div className="mr-3 h-3 w-3 rounded-full bg-green-500" />
                      <span className="font-medium text-gray-900">Waved &quot;hi&quot; to grandma</span>
                    </div>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-blue-50 p-4">
                    <div className="flex items-center">
                      <div className="mr-3 h-3 w-3 rounded-full bg-blue-500" />
                      <span className="font-medium text-gray-900">Joined in mirror play</span>
                    </div>
                    <CheckCircle className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-purple-50 p-4">
                    <div className="flex items-center">
                      <div className="mr-3 h-3 w-3 rounded-full bg-purple-500" />
                      <span className="font-medium text-gray-900">Said &quot;more&quot; during snack</span>
                    </div>
                    <CheckCircle className="h-5 w-5 text-purple-500" />
                  </div>
                </div>
              </div>
              <div className="pointer-events-none absolute -left-6 -top-6 h-24 w-24 animate-pulse rounded-full bg-gradient-to-br from-amber-300 to-orange-400 opacity-70 blur-sm" />
              <div
                className="pointer-events-none absolute -bottom-8 -right-4 h-28 w-28 animate-pulse rounded-full bg-gradient-to-br from-pink-400 to-fuchsia-500 opacity-50 blur-md"
                style={{ animationDelay: '0.5s' }}
              />
              <div className="pointer-events-none absolute right-1/4 top-1/2 h-16 w-16 rounded-full bg-cyan-300/80 opacity-60 blur-sm" />
            </div>
          </div>
        </div>
      </section>
      )}
      {!showForm && (
      <section className="relative z-10 bg-white/80 py-14 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2 text-slate-900">
            <Heart className="h-6 w-6 text-indigo-600" />
            <h2 className="text-2xl sm:text-3xl font-bold">For Parent</h2>
          </div>
          <p className="text-slate-600 text-lg mb-8">Powerful features for your child's growth.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {parentFeatures.map((f) => (
              <div key={f.title} className={`rounded-2xl border border-slate-200 p-5 bg-gradient-to-br ${f.accent} shadow-sm`}>
                <div className={`w-10 h-10 rounded-xl ${f.iconBg} flex items-center justify-center mb-3`}>
                  <f.Icon className="h-5 w-5" />
                </div>
                <h3 className="text-slate-900 font-semibold mb-1">{f.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}
      {/* Get Started Section (Signup Form) */}
      {showForm && (
      <section id="get-started" className="relative z-10 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-white/50 bg-white/90 p-8 shadow-xl backdrop-blur-md lg:p-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Ready to turn progress into celebration?
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Join thousands of families who are celebrating tiny wins every day. 
                Your child's personalized roadmap is just a few steps away.
              </p>
              {inviteLoading ? (
                <p className="mt-4 text-sm font-medium text-slate-500">Loading your invitation…</p>
              ) : null}
              {inviteError ? (
                <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                  {inviteError}
                </p>
              ) : null}
              {invitePrefill?.kidProfileName ? (
                <p className="mt-4 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-medium text-violet-800">
                  You&apos;re joining for child profile:{' '}
                  <span className="font-semibold">{invitePrefill.kidProfileName}</span>
                  . Details below are filled from your invite.
                </p>
              ) : null}
            </div>
            {showVerification ? (
              <div>
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Verify Your Email</h3>
                  <p className="text-gray-600 mb-6">
                    We've sent a verification code to <strong>{registeredEmail}</strong>
                  </p>
                  <p className="text-sm text-gray-500">
                    Please check your email and enter the 6-digit code below
                  </p>
                </div>
                <form onSubmit={handleVerificationSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="verificationCode" className="block text-sm font-semibold text-gray-700 mb-2">
                      Verification Code
                    </label>
                    <input
                      type="text"
                      id="verificationCode"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-center text-2xl tracking-widest"
                      placeholder="000000"
                      maxLength={6}
                      required
                    />
                  </div>
                  {error && <div className="text-red-600 text-center font-semibold text-sm mb-2">{error}</div>}
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8 rounded-lg text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Verifying & Setting Up Your Account...
                      </>
                    ) : (
                      <>
                        Verify & Continue
                        <ArrowRight className="inline-block ml-2 h-5 w-5" />
                      </>
                    )}
                  </button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleResendCode}
                      className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                      disabled={loading}
                    >
                      Didn't receive the code? Resend
                    </button>
                  </div>
                </form>
              </div>
            ) : success ? (
              <div className="text-green-600 text-center font-semibold text-lg mb-4">Account created! Please check your email to verify your account.</div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Enter your first name"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={form.email}
                  onChange={handleChange}
                  readOnly={emailLocked}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    emailLocked ? 'bg-slate-50 text-slate-700' : ''
                  }`}
                  placeholder="Enter your email"
                  required
                />
                {emailLocked ? (
                  <p className="mt-1 text-xs text-slate-500">Filled from your invitation email.</p>
                ) : null}
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={form.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Create a password"
                  required
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Confirm your password"
                  required
                />
              </div>
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number (optional)
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="1234567890"
                  pattern="[0-9]{10}"
                  maxLength={10}
                />
                <small className="input-help">Enter 10 digits without spaces or special characters</small>
              </div>
              {error && <div className="text-red-600 text-center font-semibold text-sm mb-2">{error}</div>}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8 rounded-lg text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Join TinyWins Today'}
                <ArrowRight className="inline-block ml-2 h-5 w-5" />
              </button>
              
              {/* Google Sign-in Button */}
              <div className="text-center my-4">
                <span className="text-gray-500 text-sm">or</span>
              </div>
              
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="w-full bg-white border-2 border-gray-300 text-gray-700 py-4 px-8 rounded-lg text-lg font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
                disabled={loading}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
              
              <p className="text-center text-sm text-gray-500 mt-4">
                Free to get started • No credit card required • No waitlist
              </p>
            </form>
            )}
          </div>
        </div>
      </section>
      )}
      {/* Use the shared FooterModern only from App.tsx */}
    </div>
  );
}

export default LandingParentReferral; 
